/**
 * Permissions Context
 * Resolves allowed permissions from the **active role** only (ActiveRoleContext), mapped to the
 * RBAC roles API response. Only the active role's permissions are set to true; all others false.
 * Use PERMISSION constants with hasPermission(PERMISSION.X) throughout the app.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { ActiveRoleContext } from './ActiveRoleContext';
import { useRoleDefinitions } from '../hooks/useRoleDefinitions';
import { getCachedRoles } from '../utils/rolesCache.utils';
import { PERMISSION_CODES, type PermissionCode } from '../constants/permissions';

export type PermissionMap = Record<string, boolean>;

interface PermissionsContextType {
  /** Resolved permission flags: PERMISSION code -> true if allowed for active role, false otherwise */
  permissions: PermissionMap;
  /** Check if the user has the given permission for the active role (use PERMISSION.X) */
  hasPermission: (permissionCode: string) => boolean;
  /** True while roles are loading and permissions are not yet resolved */
  isLoading: boolean;
  /** Active role name used to resolve permissions (from ActiveRoleContext) */
  activeRoleName: string | null;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

/**
 * Resolve the set of permission codes for a single role by mapping to the roles API response.
 * Matches the role in cache by code first (RBAC primary key), then by name; uses trimmed comparison.
 */
function resolvePermissionSetForRole(roleValue: string | null): Set<string> {
  const key = roleValue?.trim();
  if (!key) {
    return new Set();
  }
  const cached = getCachedRoles();
  if (!cached || cached.length === 0) {
    return new Set();
  }
  const role = cached.find(
    (r) =>
      (r.code && r.code.trim() === key) ||
      (r.name && r.name.trim() === key) ||
      (r.code && key.toLowerCase() === r.code.trim().toLowerCase()) ||
      (r.name && key.toLowerCase() === r.name.trim().toLowerCase())
  );
  if (!role?.permissionCodes || !Array.isArray(role.permissionCodes)) {
    return new Set();
  }
  return new Set(role.permissionCodes);
}

/**
 * Build a map of each known PERMISSION constant to true/false.
 * Only permissions in allowedSet are true; rest are false.
 */
function buildPermissionMap(allowedSet: Set<string>): PermissionMap {
  const map: PermissionMap = {};
  for (const code of PERMISSION_CODES as readonly string[]) {
    map[code] = allowedSet.has(code);
  }
  return map;
}

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use useContext so we can render without throwing when outside ActiveRoleProvider
  // (e.g. during viewType toggle / concurrent recovery). Safe fallback: no active role.
  const activeRoleContext = useContext(ActiveRoleContext);
  const activeRole = activeRoleContext?.activeRole ?? null;
  const availableRoles = activeRoleContext?.availableRoles ?? [];
  const { data: rolesData, isLoading: isRolesLoading } = useRoleDefinitions();

  const value = useMemo<PermissionsContextType>(() => {
    const roleForResolution =
      activeRole ?? (availableRoles.length > 0 ? availableRoles[0] : null);
    const activeRoleName =
      typeof roleForResolution === 'string' ? roleForResolution : null;
    const allowedSet = resolvePermissionSetForRole(activeRoleName);
    const permissions = buildPermissionMap(allowedSet);

    const hasPermission = (permissionCode: string): boolean => {
      return permissions[permissionCode] === true;
    };

    return {
      permissions,
      hasPermission,
      isLoading: isRolesLoading,
      activeRoleName,
    };
  }, [activeRole, availableRoles, rolesData, isRolesLoading]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

/**
 * Hook that returns whether the current user has a specific permission.
 * Use with PERMISSION constants: useHasPermission(PERMISSION.User_View)
 */
export const useHasPermission = (permissionCode: PermissionCode | string): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(permissionCode);
};
